import Service, { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import type StoreService from '@ember-data/store';
import type ReportTrackingModel from 'tio-data/models/report-tracking';
import type { ReportViewActivity } from 'tio-data/models/report-tracking';
import { getBrowserMeta } from 'tio-common/utils/browser-meta';

const DEBOUNCE_MS = 2000;

export interface ReportTrackingSession {
  end: () => void;
}

interface TrackingSessionState {
  trackingRecordId: string | null;
  sessionReady: boolean;
  pendingActivity: Partial<ReportViewActivity>;
  debounceTimer: ReturnType<typeof setTimeout> | null;
  isFlushing: boolean;
  hasEnded: boolean;
}

export default class ReportTrackingService extends Service {
  @service declare store: StoreService;

  private activeSession: TrackingSessionState | null = null;

  constructor(owner: Owner) {
    super(owner);
    registerDestructor(this, () => this.endSession());
  }

  startSession(reportId: string, viewedFrom: 'admin' | 'partner'): ReportTrackingSession {
    // End any prior session before starting a new one
    this.endSession();

    const session: TrackingSessionState = {
      trackingRecordId: null,
      sessionReady: false,
      pendingActivity: {},
      debounceTimer: null,
      isFlushing: false,
      hasEnded: false,
    };
    this.activeSession = session;

    const activity: ReportViewActivity = {
      viewed_at: new Date().toISOString(),
      viewed_from: viewedFrom,
      filters_applied: [],
      sorts_applied: [],
      exports: [],
    };

    void this.createTrackingRecord(session, reportId, activity);

    return {
      end: () => this.endTrackingSession(session),
    };
  }

  trackFilter(field: string, value: unknown, label?: string) {
    const session = this.activeSession;
    if (!session) return;

    const entry = { field, label: label || field, value, at: new Date().toISOString() };
    const pending = session.pendingActivity.filters_applied ?? [];
    pending.push(entry);
    session.pendingActivity.filters_applied = pending;
    if (session.sessionReady) this.scheduleFlush(session);
  }

  trackSort(field: string, direction: string, label?: string) {
    const session = this.activeSession;
    if (!session) return;

    const entry = { field, label: label || field, direction, at: new Date().toISOString() };
    const pending = session.pendingActivity.sorts_applied ?? [];
    pending.push(entry);
    session.pendingActivity.sorts_applied = pending;
    if (session.sessionReady) this.scheduleFlush(session);
  }

  trackExport(format: string) {
    const session = this.activeSession;
    if (!session) return;

    const entry = { format, at: new Date().toISOString() };
    const pending = session.pendingActivity.exports ?? [];
    pending.push(entry);
    session.pendingActivity.exports = pending;
    if (session.sessionReady) this.scheduleFlush(session);
  }

  endSession() {
    this.endTrackingSession(this.activeSession);
  }

  // --- Private ---

  private async createTrackingRecord(
    session: TrackingSessionState,
    reportId: string,
    activity: ReportViewActivity
  ) {
    const trackingRecord = this.store.createRecord('report-tracking', {
      reportId,
      reportViewActivity: activity,
      browserMeta: getBrowserMeta(),
    }) as ReportTrackingModel;

    try {
      await trackingRecord.save();
    } catch (e: unknown) {
      if (!session.hasEnded && this.activeSession === session) {
        console.warn('[report-tracking] Failed to create tracking record', e);
      }
      session.trackingRecordId = null;
      session.sessionReady = false;
      return;
    }

    session.trackingRecordId = trackingRecord.id;
    session.sessionReady = true;

    if (this.hasPendingActivity(session)) {
      void this.flush(session);
    }
  }

  private endTrackingSession(session: TrackingSessionState | null) {
    if (!session || session.hasEnded) return;

    session.hasEnded = true;
    if (this.activeSession === session) {
      this.activeSession = null;
    }

    if (session.debounceTimer) {
      clearTimeout(session.debounceTimer);
      session.debounceTimer = null;
    }

    if (session.sessionReady && this.hasPendingActivity(session)) {
      void this.flush(session);
    }
  }

  private scheduleFlush(session: TrackingSessionState) {
    if (session.hasEnded) {
      void this.flush(session);
      return;
    }

    if (session.debounceTimer) {
      clearTimeout(session.debounceTimer);
    }
    session.debounceTimer = setTimeout(() => {
      void this.flush(session);
    }, DEBOUNCE_MS);
  }

  private async flush(session: TrackingSessionState) {
    if (session.debounceTimer) {
      clearTimeout(session.debounceTimer);
      session.debounceTimer = null;
    }

    if (!session.trackingRecordId || session.isFlushing || !this.hasPendingActivity(session))
      return;

    const pending = session.pendingActivity;
    session.pendingActivity = {};
    session.isFlushing = true;

    const trackingRecord = this.store.peekRecord(
      'report-tracking',
      session.trackingRecordId
    ) as ReportTrackingModel | null;

    if (!trackingRecord) {
      session.isFlushing = false;
      return;
    }

    const existing = trackingRecord.reportViewActivity ?? ({} as ReportViewActivity);
    trackingRecord.reportViewActivity = {
      ...existing,
      filters_applied: [...(existing.filters_applied ?? []), ...(pending.filters_applied ?? [])],
      sorts_applied: [...(existing.sorts_applied ?? []), ...(pending.sorts_applied ?? [])],
      exports: [...(existing.exports ?? []), ...(pending.exports ?? [])],
    };

    try {
      await trackingRecord.save();
    } catch (e: unknown) {
      console.warn('[report-tracking] Failed to update tracking record', e);
    } finally {
      session.isFlushing = false;
      if (this.hasPendingActivity(session)) {
        if (session.hasEnded) {
          void this.flush(session);
        } else {
          this.scheduleFlush(session);
        }
      }
    }
  }

  private hasPendingActivity(session: TrackingSessionState) {
    return Boolean(
      session.pendingActivity.filters_applied?.length ||
      session.pendingActivity.sorts_applied?.length ||
      session.pendingActivity.exports?.length
    );
  }
}
